import type { CMCodeRef } from "@app/portal-sdk";
import { Chip, Typography } from "@app/ui-kit";
import Tooltip from "@mui/material/Tooltip";
import { useDd } from "../lib/dd";

/** A code as the client's word for it, with the list's description on hover. Never a bare code. */
export const Word = ({ domain, value }: { domain: string; value: CMCodeRef | null | undefined }) => {
  const { describe } = useDd();
  if (!value) return <Typography component="span" variant="body2" color="text.disabled">—</Typography>;
  const description = describe(domain, value.code);
  const node = <Typography component="span" variant="body2">{value.label}</Typography>;
  return description ? <Tooltip title={description}>{node}</Tooltip> : node;
};

/** A labelled attribute: "Confidentiality: Code name only", the description on hover. Decision 107: an attribute is data, never a chip. */
const Labelled = ({ label, value, title, testId }: { label: string; value: string; title?: string; testId?: string }) => {
  const node = <Typography component="span" variant="body2" data-testid={testId}><Typography component="span" variant="body2" color="text.secondary">{label}: </Typography>{value}</Typography>;
  return title ? <Tooltip title={title}>{node}</Tooltip> : node;
};
/** DDE-7: the confidentiality beside the status on every screen of the engagement, in the words the deal team is told. */
export const ConfidentialityChip = ({ value, bare = false }: { value: CMCodeRef | null; bare?: boolean }) => {
  const { content } = useDd();
  if (!value) return null;
  const entry = content.confidentiality.find((c) => c.id === value.code);
  const title = entry ? `${entry.description} ${entry.detail ?? ""}` : value.label;
  return bare ? <Tooltip title={title}><Typography component="span" variant="body2" data-testid="confidentiality-chip">{value.label}</Typography></Tooltip> : <Labelled label="Confidentiality" value={value.label} title={title} testId="confidentiality-chip" />;
};
export const Muted = ({ children }: { children: React.ReactNode }) => <Typography component="span" variant="body2" color="text.secondary">{children}</Typography>;

/** A workstream's verdict, or what fills it and when (decision 46: an empty judgement says what fills it). Never a number. */
export const VerdictChip = ({ value }: { value: CMCodeRef | null }) => {
  const { content } = useDd();
  return value ? <Chip label={value.label} size="small" variant="outlined" sx={{ height: 22, fontSize: 11 }} /> : <Typography component="span" variant="body2" color="text.disabled" title={content.sentences.verdictNotYet} data-testid="verdict-not-yet">verdict not yet</Typography>;
};
/** A review area's rating, or what fills it and when. Never averaged. */
export const RatingWord = ({ value }: { value: CMCodeRef | null }) => {
  const { content } = useDd();
  return value ? <Typography component="span" variant="body2">{value.label}</Typography> : <Typography component="span" variant="body2" color="text.disabled" title={content.sentences.ratingNotYet} data-testid="rating-not-yet">rating not yet</Typography>;
};

/** DDP-8: the deal's stage, visibly a different thing from ours: labelled "Deal stage", never in the gate rail. */
export const DealChip = ({ value, bare = false }: { value: CMCodeRef | null; bare?: boolean }) => {
  const { describe } = useDd();
  const label = value ? value.label : "Not set";
  const description = value ? describe("dd_deal_stage", value.code) : null;
  return bare ? (description ? <Tooltip title={description}><Typography component="span" variant="body2" data-testid="deal-chip">{label}</Typography></Tooltip> : <Typography component="span" variant="body2" data-testid="deal-chip">{label}</Typography>) : <Labelled label="Deal stage" value={label} title={description ?? undefined} testId="deal-chip" />;
};

/** DDP-9: the closure outcome, labelled; on hold is the one that leaves the engagement open. */
export const OutcomeChip = ({ value }: { value: CMCodeRef | null | undefined }) => (value ? <Labelled label="Outcome" value={value.label} testId="outcome-chip" /> : null);

/** LIB-5: what a question is probing, shown wherever a question is shown. The client's word, the list's description on hover. */
export const LensChip = ({ value }: { value: CMCodeRef | null | undefined }) => {
  const { describe } = useDd();
  if (!value) return null;
  const node = <Chip label={value.label} size="small" variant="outlined" color="secondary" sx={{ height: 20, fontSize: 10.5 }} data-testid="lens-chip" />;
  const description = describe("dd_question_lens", value.code);
  return description ? <Tooltip title={description}>{node}</Tooltip> : node;
};

/** LIB-3, DDP-7: where a row came from, in one word. */
export const OriginChip = ({ origin, long = false }: { origin: "library" | "own"; long?: boolean }) => (
  <Chip label={origin === "library" ? (long ? "From the library" : "Library") : "Yours"} size="small" variant={origin === "library" ? "outlined" : "filled"} sx={{ height: 18, fontSize: 10 }} data-testid="origin-chip" />
);

export type StateKind = "priority" | "materiality" | "questionStatus" | "criticality" | "chaseChannel" | "participantStatus" | "sessionStatus" | "findingStatus" | "costBasis" | "synergyType" | "remedy" | "riskStatus" | "duplicationStatus" | "inventorySide" | "horizon";
/** LIB-4: one of the application's own states on an engagement question, as its label from the context, never a bare code. */
export const StateWord = ({ kind, code }: { kind: StateKind; code: string }) => {
  const { content } = useDd();
  const entry = content.states[kind].find((s) => s.id === code);
  const node = <Typography component="span" variant="body2">{entry?.label ?? code}</Typography>;
  return entry ? <Tooltip title={entry.description}>{node}</Tooltip> : node;
};
/**
 * The position of a value in its fixed set, for a table cell: a status column sorts as its list reads, never
 * alphabetically. The row builder calls this and wraps the cell in `Sorted`, because the table reads the sort value
 * from the cell element it is given, not from what a component renders inside it.
 */
export const useOrder = () => {
  const { content, options } = useDd();
  return {
    state: (kind: StateKind, code: string | null | undefined): number => { const at = code ? content.states[kind].findIndex((s) => s.id === code) : -1; return at < 0 ? 999 : at; },
    code: (domain: string, value: CMCodeRef | null | undefined): number => { const at = value ? options(domain).findIndex((o) => o.code === value.code) : -1; return at < 0 ? 999 : at; }
  };
};
/** The same word in a table cell. Wrap it in `Sorted` with `useOrder().state(kind, code)` so the column sorts in the set's order. */
export const SetCell = ({ kind, code, testId }: { kind: StateKind; code: string; testId?: string }) => <span data-testid={testId}><StateWord kind={kind} code={code} /></span>;
/** A code from a list (lens, severity, confidence) in a table cell. Wrap it in `Sorted` with `useOrder().code(domain, value)` so the column sorts in the list's order. */
export const CodeCell = ({ domain, value, testId }: { domain: string; value: CMCodeRef | null | undefined; testId?: string }) => {
  const { describe } = useDd();
  if (!value) return <span data-testid={testId} />;
  const description = describe(domain, value.code);
  const node = <Typography component="span" variant="body2" data-testid={testId}>{value.label}</Typography>;
  return description ? <Tooltip title={description}>{node}</Tooltip> : node;
};
